// The map behind every Remote Punch view: a pin exactly where the punch was
// taken, the GPS accuracy as a circle around it, a metric scale, a recenter
// button and an accuracy badge.
//
// Leaflet (and its stylesheet) is imported on first render only, so it is a
// separate chunk that a page never downloads until a map is actually shown.
// Tiles are OpenStreetMap's standard layer (attribution kept, as its usage
// policy requires), shown as-is in both themes: inverting a map's colours for
// dark mode makes street names and landmarks harder to read, which defeats
// the point of showing where someone was.
import React, { useEffect, useRef, useState } from "react";
import { LocateFixed } from "lucide-react";

let leafletPromise = null;
const loadLeaflet = () => {
  if (!leafletPromise) {
    leafletPromise = Promise.all([import("leaflet"), import("leaflet/dist/leaflet.css")])
      .then(([mod]) => mod.default || mod)
      .catch((err) => {
        leafletPromise = null;
        throw err;
      });
  }
  return leafletPromise;
};

/** Pin shaped like a map marker, tip on the exact point, with a soft pulse. */
const pinHtml = (color) => `
  <div class="rp-pin">
    <span class="rp-pin-pulse" style="background:${color}"></span>
    <svg width="34" height="44" viewBox="0 0 34 44" aria-hidden="true">
      <path d="M17 43C17 43 32 27.6 32 17A15 15 0 0 0 2 17C2 27.6 17 43 17 43Z" fill="${color}" stroke="#ffffff" stroke-width="2.5"/>
      <circle cx="17" cy="17" r="6" fill="#ffffff"/>
    </svg>
  </div>`;

const MAP_CSS = `
  .rp-map .leaflet-container{font-family:inherit}
  .rp-map .rp-pin{position:relative;width:34px;height:44px}
  .rp-map .rp-pin svg{position:absolute;inset:0;filter:drop-shadow(0 3px 4px rgba(0,0,0,.35))}
  .rp-map .rp-pin-pulse{position:absolute;left:50%;bottom:-6px;width:16px;height:16px;margin-left:-8px;border-radius:50%;opacity:.45;animation:rp-pulse 2s ease-out infinite}
  @keyframes rp-pulse{0%{transform:scale(.6);opacity:.55}100%{transform:scale(2.6);opacity:0}}
  .rp-map .leaflet-control-scale-line{border-color:#334155;color:#0f172a;background:rgba(255,255,255,.85);font-weight:600}
  .rp-map .leaflet-bar a{color:#0f172a}
  .rp-map-dark .leaflet-tile-pane{filter:brightness(.92) saturate(.95)}
`;

/** Accuracy → a word and a colour (the same bands the attendance page uses). */
export const accuracyQuality = (meters) => {
  if (!Number.isFinite(meters)) return { level: "unknown", color: "#64748b" };
  if (meters <= 20) return { level: "excellent", color: "#059669" };
  if (meters <= 50) return { level: "good", color: "#16a34a" };
  if (meters <= 100) return { level: "fair", color: "#d97706" };
  return { level: "poor", color: "#dc2626" };
};

export default function PunchMap({
  latitude,
  longitude,
  accuracyMeters,
  height = 280,
  dark = false,
  failedText,
  recenterLabel = "Recenter",
  accuracyLabel = null,
  pinColor = "#dc2626",
  interactive = true,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const frameRef = useRef(null);
  const [failed, setFailed] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return undefined;
    let cancelled = false;

    loadLeaflet()
      .then((L) => {
        if (cancelled || !containerRef.current) return;
        const map = L.map(containerRef.current, {
          zoomControl: interactive,
          attributionControl: true,
          scrollWheelZoom: false,
          dragging: interactive,
          doubleClickZoom: interactive,
          touchZoom: interactive,
          keyboard: interactive,
        });
        mapRef.current = map;
        map.attributionControl.setPrefix(false);
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          maxZoom: 19,
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a>',
        }).addTo(map);
        if (interactive) L.control.scale({ metric: true, imperial: false, position: "bottomleft" }).addTo(map);

        const center = [latitude, longitude];
        const radius = Number.isFinite(accuracyMeters) && accuracyMeters > 0 ? accuracyMeters : null;
        if (radius) {
          L.circle(center, {
            radius,
            color: "#2563eb",
            weight: 1.5,
            dashArray: "4 4",
            fillColor: "#3b82f6",
            fillOpacity: 0.14,
            interactive: false,
          }).addTo(map);
        }
        L.marker(center, {
          icon: L.divIcon({ className: "", html: pinHtml(pinColor), iconSize: [34, 44], iconAnchor: [17, 43] }),
          keyboard: false,
          interactive: false,
        }).addTo(map);

        // Frame the accuracy circle with room for the pin; street level at most.
        // Bounds come from the position itself (a circle's own getBounds needs
        // the map to have a view already).
        const frame = radius ? L.latLng(center).toBounds(Math.max(radius, 40) * 2) : null;
        frameRef.current = () => {
          if (frame) map.fitBounds(frame, { padding: [48, 48], maxZoom: 18 });
          else map.setView(center, 17);
        };
        frameRef.current();
        setReady(true);
        // Whenever the box changes size (dialog opening, phone rotation, a
        // responsive layout switch) Leaflet must re-measure, or the pin
        // drifts off-centre.
        let resizeTimer = null;
        const refit = () => {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(() => {
            if (!mapRef.current) return;
            mapRef.current.invalidateSize();
            frameRef.current?.();
          }, 120);
        };
        if (typeof ResizeObserver !== "undefined") {
          const observer = new ResizeObserver(refit);
          observer.observe(containerRef.current);
          map.on("unload", () => observer.disconnect());
        }
        refit();
      })
      .catch((err) => {
        console.error("Remote punch map failed:", err);
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (mapRef.current) mapRef.current.remove();
      mapRef.current = null;
      setReady(false);
    };
  }, [latitude, longitude, accuracyMeters, pinColor, interactive]);

  if (failed) {
    return (
      <div style={{ height, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 14, background: dark ? "rgba(255,255,255,0.04)" : "#f1f5f9", color: dark ? "#94a3b8" : "#64748b", fontSize: 13 }}>
        {failedText}
      </div>
    );
  }

  const quality = accuracyQuality(accuracyMeters);

  return (
    <div
      className={dark ? "rp-map rp-map-dark" : "rp-map"}
      style={{
        position: "relative",
        height,
        width: "100%",
        borderRadius: 14,
        overflow: "hidden",
        isolation: "isolate",
        border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)"}`,
        background: dark ? "#1e293b" : "#e2e8f0",
      }}
    >
      <style>{MAP_CSS}</style>
      {/* Leaflet assumes left-to-right; the overlays below follow the page. */}
      <div ref={containerRef} dir="ltr" style={{ position: "absolute", inset: 0, zIndex: 0 }} />

      {ready && accuracyLabel && Number.isFinite(accuracyMeters) && (
        <div
          dir="ltr"
          style={{
            position: "absolute", top: 10, right: 10, zIndex: 500,
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "5px 10px", borderRadius: 999,
            background: "rgba(255,255,255,0.94)", color: "#0f172a",
            fontSize: 12, fontWeight: 700, boxShadow: "0 2px 8px rgba(15,23,42,0.18)",
          }}
        >
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: quality.color }} />
          <span>± {accuracyMeters} m</span>
          <span style={{ fontWeight: 500, color: "#475569" }}>· {accuracyLabel}</span>
        </div>
      )}

      {ready && interactive && (
        <button
          type="button"
          onClick={() => frameRef.current?.()}
          title={recenterLabel}
          aria-label={recenterLabel}
          style={{
            position: "absolute", bottom: 26, right: 10, zIndex: 500,
            width: 34, height: 34, borderRadius: 10, border: "none", cursor: "pointer",
            background: "rgba(255,255,255,0.96)", color: "#0f172a",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 2px 8px rgba(15,23,42,0.22)",
          }}
        >
          <LocateFixed size={17} />
        </button>
      )}
    </div>
  );
}
